﻿import {StringNamedValue} from "@2d-front-monorepo/core";
import PaymentStatus from "../../enums/PaymentStatus";
import {PermitId} from "../../nominalTypes/PermitId";
import {AllPermitsResult} from "../../types/AllPermitsResult";
import {CreatePermitArguments} from "../../types/arguments/CreatePermitArguments";
import {
	GetPermitRecurrenceArguments,
	PermitRecurrenceSegment
} from "../../types/arguments/GetPermitRecurrenceArguments";
import {GetPermitsByAddressArguments} from "../../types/arguments/GetPermitsByAddressArguments";
import {QueryAddressesArguments} from "../../types/arguments/QueryAddressesArguments";
import {ReplaceDriverArguments} from "../../types/arguments/ReplaceDriverArguments";
import {ReplaceResidencyArguments} from "../../types/arguments/ReplaceResidencyArguments";
import {ReplaceVehicleArguments} from "../../types/arguments/ReplaceVehicleArguments";
import {CreatePermitResult} from "../../types/CreatePermitResult";
import {PaymentHistoryEntry} from "../../types/PaymentHistoryEntry";
import {PermitResidencyResult} from "../../types/PermitResidencyResult";
import {PermitResult} from "../../types/PermitResult";
import {QueryAddressResult} from "../../types/QueryAddressResult";
import IPermitsPermits from "../IPermitsPermits";
import {PermitsFrontClient} from "./PermitsFrontClient";

export class PermitsPermits implements IPermitsPermits {
	_client: PermitsFrontClient;

	constructor(client: PermitsFrontClient) {
		this._client = client;
	}

	addresses(data: QueryAddressesArguments, signal?: AbortSignal | undefined): Promise<QueryAddressResult[]> {
		return this._client.jsonFetch("permits/addresses", "POST", data, {
			signal: signal
		});
	}

	archived(signal?: AbortSignal | undefined): Promise<PermitResult[]> {
		return this._client.jsonFetch("permits/archived", "GET", null, {
			signal: signal
		});
	}

	bill(permitId: PermitId, signal?: AbortSignal | undefined): Promise<PaymentStatus> {
		return this._client.jsonFetch(`permits/${permitId}/bill`, "PUT", null, {
			signal: signal
		});
	}

	cancel(permitId: PermitId, signal?: AbortSignal | undefined): Promise<void> {
		return this._client.nullFetch(`permits/${permitId}/cancel`, "PUT", null, {
			signal: signal
		});
	}

	bulkCancel(permitIds: PermitId[], signal?: AbortSignal | undefined): Promise<void> {
		return this._client.nullFetch(`permits/cancel`, "PUT", permitIds, {
			signal: signal
		});
	}

	create(data: CreatePermitArguments, signal?: AbortSignal | undefined): Promise<CreatePermitResult> {
		return this._client.jsonFetch("permits", "POST", data, {
			signal: signal
		});
	}

	findByAddress(data: GetPermitsByAddressArguments, signal?: AbortSignal | undefined): Promise<PermitResidencyResult[]> {
		return this._client.jsonFetch("permits/find-by/address", "POST", data, {
			signal: signal
		});
	}

	getInvoice(permitId: PermitId, signal?: AbortSignal | undefined): Promise<string> {
		return this._client.textFetch(`permits/${permitId}/invoice`, "GET", null, {
			signal: signal
		});
	}

	getPayments(permitId: PermitId, signal?: AbortSignal | undefined): Promise<PaymentHistoryEntry[]> {
		return this._client.jsonFetch(`permits/${permitId}/payments`, "GET", null, {
			signal: signal
		});
	}

	groups(group: string, signal?: AbortSignal | undefined): Promise<StringNamedValue[]> {
		return this._client.jsonFetch(`permits/lists/${group}`, "GET", null, {
			signal: signal
		});
	}

	list(signal?: AbortSignal | undefined): Promise<AllPermitsResult> {
		return this._client.jsonFetch("permits", "GET", null, {
			signal: signal
		});
	}

	listPayments(signal?: AbortSignal | undefined): Promise<PaymentHistoryEntry[]> {
		return this._client.jsonFetch("permits/payments", "GET", null, {
			signal: signal
		});
	}

	listRecurrences(permitId: PermitId, data: GetPermitRecurrenceArguments, signal?: AbortSignal | undefined): Promise<PermitRecurrenceSegment[]> {
		return this._client.jsonFetch(`permits/${permitId}/payments`, "GET", data, {
			signal: signal
		});
	}

	replaceDriver(permitId: PermitId, data: ReplaceDriverArguments, signal?: AbortSignal | undefined): Promise<void> {
		return this._client.nullFetch(`permits/${permitId}/replace-driver`, "PUT", data, {
			signal: signal
		});
	}

	replaceResidency(permitId: PermitId, data: ReplaceResidencyArguments, signal?: AbortSignal | undefined): Promise<void> {
		return this._client.nullFetch(`permits/${permitId}/replace-residency`, "PUT", data, {
			signal: signal
		});
	}

	replaceVehicle(permitId: PermitId, data: ReplaceVehicleArguments, signal?: AbortSignal | undefined): Promise<void> {
		return this._client.nullFetch(`permits/${permitId}/replace-vehicle`, "PUT", data, {
			signal: signal
		});
	}

	submitForReview(permitId: PermitId, signal?: AbortSignal | undefined): Promise<void> {
		return this._client.nullFetch(`permits/${permitId}/re-submit`, "PUT", null, {
			signal: signal
		});
	}
}
